import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { Search, Loader2, FileText, Zap, Layers, User, AlertCircle } from 'lucide-react';

const severityBadge = (sev) => {
  const map = { High: 'bg-red-100 text-red-700', Medium: 'bg-amber-100 text-amber-700', Low: 'bg-emerald-100 text-emerald-700' };
  return map[sev] || 'bg-gray-100 text-gray-700';
};

export default function MatchMemory() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const data = await api.searchMemory(q);
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const hasResults = results && (
    results.reports?.length
    || results.moments?.length
    || results.patterns?.length
    || results.playerReviews?.length
    || results.findings?.length
    || results.decisions?.length
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
          <Search className="w-7 h-7 text-navy-600" /> Match Memory
        </h1>
        <p className="text-gray-500 text-sm mt-1">Search reports, moments, patterns, player reviews, coaching findings, and decisions.</p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            className="w-full bg-white border border-gray-300 rounded-xl pl-11 pr-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 text-base"
            placeholder="Search for tactical themes, player names, moments..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <button type="submit" disabled={loading || !query.trim()} className="bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-xl transition-colors flex items-center gap-2">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} Search
        </button>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-navy-400 animate-spin" /></div>
      ) : searched && !hasResults ? (
        <div className="text-center py-16 border border-dashed border-gray-300 rounded-xl">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No results found</p>
          <p className="text-gray-400 text-sm mt-1">Try a different keyword or phrase.</p>
        </div>
      ) : results ? (
        <div className="space-y-8">
          {/* Reports */}
          {results.reports?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-navy-900 font-semibold text-lg mb-3">
                <FileText className="w-5 h-5 text-navy-600" /> Reports ({results.reports.length})
              </h2>
              <div className="space-y-3">
                {results.reports.map((r) => (
                  <Link key={r.id} to={`/analysis/${r.match_id}`} className="block border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-navy-900">{r.report_title || 'Untitled report'}</h3>
                      <span className="text-gray-400 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    {r.excerpt && <p className="text-gray-600 text-sm line-clamp-2">{r.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Key Moments */}
          {results.moments?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-navy-900 font-semibold text-lg mb-3">
                <Zap className="w-5 h-5 text-navy-600" /> Key Moments ({results.moments.length})
              </h2>
              <div className="space-y-3">
                {results.moments.map((m) => (
                  <Link key={m.id} to={`/analysis/${m.match_id}`} className="block border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-1">
                      {m.minute && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-navy-100 text-navy-700">{m.minute}'</span>}
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{m.moment_type}</span>
                    </div>
                    {m.what_happened && <p className="text-gray-700 text-sm"><span className="font-medium">What happened:</span> {m.what_happened}</p>}
                    {m.coach_observation && <p className="text-gray-600 text-sm mt-1"><span className="font-medium">Coach:</span> {m.coach_observation}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Patterns */}
          {results.patterns?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-navy-900 font-semibold text-lg mb-3">
                <Layers className="w-5 h-5 text-navy-600" /> Patterns ({results.patterns.length})
              </h2>
              <div className="space-y-3">
                {results.patterns.map((p) => (
                  <Link key={p.id} to="/patterns" className="block border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-navy-900 flex-1">{p.pattern_name}</h3>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{p.occurrence_count}× seen</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${severityBadge(p.severity)}`}>{p.severity}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Player Reviews */}
          {results.playerReviews?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-navy-900 font-semibold text-lg mb-3">
                <User className="w-5 h-5 text-navy-600" /> Player Reviews ({results.playerReviews.length})
              </h2>
              <div className="space-y-3">
                {results.playerReviews.map((r) => (
                  <Link key={r.id} to={`/analysis/${r.match_id}`} className="block border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-navy-900">{r.player_name}</h3>
                      {r.position_or_role && <span className="text-gray-400 text-sm">{r.position_or_role}</span>}
                    </div>
                    {r.excerpt && <p className="text-gray-600 text-sm">{r.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Coaching findings */}
          {results.findings?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-navy-900 font-semibold text-lg mb-3">
                <Layers className="w-5 h-5 text-navy-600" /> Coaching Findings ({results.findings.length})
              </h2>
              <div className="space-y-3">
                {results.findings.map((f) => (
                  <Link key={f.id} to={f.match_id ? `/analysis/${f.match_id}` : '/memory'} className="block border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-navy-900">{f.title}</h3>
                      {f.staff_status && <span className="px-2 py-0.5 rounded-full text-xs bg-navy-50 text-navy-700">{f.staff_status}</span>}
                      {f.recurrence_class && <span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-800">{f.recurrence_class}</span>}
                    </div>
                    {f.excerpt && <p className="text-gray-600 text-sm">{f.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Coaching decisions */}
          {results.decisions?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-navy-900 font-semibold text-lg mb-3">
                <FileText className="w-5 h-5 text-navy-600" /> Coaching Decisions ({results.decisions.length})
              </h2>
              <div className="space-y-3">
                {results.decisions.map((d) => (
                  <Link key={d.id} to={d.match_id ? `/analysis/${d.match_id}` : '/memory'} className="block border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-navy-900 line-clamp-2">{d.decision}</h3>
                      {d.status && <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-800 whitespace-nowrap">{d.status}</span>}
                    </div>
                    {d.excerpt && <p className="text-gray-600 text-sm">{d.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="text-center py-16 border border-dashed border-gray-300 rounded-xl">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Search your match memory</p>
          <p className="text-gray-400 text-sm mt-1">Enter a keyword above to search across all reports, moments, and patterns.</p>
        </div>
      )}
    </div>
  );
}
